import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2, Save, X, UserCircle, Upload, Settings } from "lucide-react";
import { useContent } from "@/context/ContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function getImageUrl(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
}

const EMPTY_MONITOR = { nombre: "", especialidad: "", descripcion: "", foto_url: "" };

export default function MonitorsManager() {
  const { content, updateSection } = useContent();

  // ── Section header state ───────────────────────────────────────────────────
  const [sectionData, setSectionData] = useState({ section_label: "", title: "", subtitle: "" });
  const [savingSection, setSavingSection] = useState(false);
  const [savedSection, setSavedSection] = useState(false);
  // Ref para inicializar sectionData solo una vez (evita sobreescribir input del usuario)
  const sectionInitialized = useRef(false);

  useEffect(() => {
    if (content.monitors && !sectionInitialized.current) {
      sectionInitialized.current = true;
      setSectionData({
        section_label: content.monitors.section_label || "Nuestro Equipo",
        title: content.monitors.title || "Los mejores monitores",
        subtitle: content.monitors.subtitle || "Profesionales titulados y apasionados de la educación al aire libre.",
      });
    }
  }, [content.monitors]);

  const handleSectionChange = (e) => setSectionData(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSaveSection = async () => {
    setSavingSection(true);
    try {
      await updateSection("monitors", sectionData);
      setSavedSection(true);
      setTimeout(() => setSavedSection(false), 3000);
    } catch (e) { console.error(e); }
    finally { setSavingSection(false); }
  };

  // ── Monitors CRUD state ────────────────────────────────────────────────────
  const [monitors, setMonitors] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_MONITOR);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fetchMonitors = () =>
    axios.get(`${API}/monitors`).then(r => setMonitors(r.data)).catch(() => {});

  useEffect(() => { fetchMonitors(); }, []);

  const handleEdit = (m) => {
    setEditing(m._id);
    setForm({ nombre: m.nombre, especialidad: m.especialidad, descripcion: m.descripcion || "", foto_url: m.foto_url || "" });
  };
  const handleAdd = () => { setEditing("__new__"); setForm(EMPTY_MONITOR); };
  const handleCancel = () => { setEditing(null); setForm(EMPTY_MONITOR); };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing === "__new__") {
        await axios.post(`${API}/monitors`, form);
      } else {
        await axios.put(`${API}/monitors/${editing}`, form);
      }
      await fetchMonitors();
      setEditing(null);
      setForm(EMPTY_MONITOR);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este monitor?")) return;
    await axios.delete(`${API}/monitors/${id}`);
    await fetchMonitors();
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/media/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = res.data.url.startsWith("http") ? res.data.url : `${BACKEND_URL}${res.data.url}`;
      setForm(p => ({ ...p, foto_url: url }));
    } catch (e) { console.error(e); }
    finally { setUploading(false); }
  };

  return (
    <div data-testid="monitors-manager">
      {/* ── Page header ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-['Montserrat'] font-bold text-2xl text-[#0F172A] mb-1">Monitores</h1>
          <p className="text-[#475569] font-['Montserrat'] text-sm">Gestiona el equipo y el contenido de la sección</p>
        </div>
        <button onClick={handleAdd} data-testid="add-monitor-btn"
          className="flex items-center gap-2 bg-[#0072A4] text-white font-['Montserrat'] font-semibold px-4 py-2 rounded-lg hover:bg-[#005A80] transition-all text-sm">
          <Plus size={16} /> Añadir monitor
        </button>
      </div>

      {/* ── Section header editor ────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-[#E2E8F0] p-6 mb-6" data-testid="monitors-section-editor">
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#E2E8F0]">
          <div className="flex items-center gap-2">
            <Settings size={15} color="#0072A4" />
            <h3 className="font-['Montserrat'] font-semibold text-[#0F172A] text-base">Textos de la sección</h3>
          </div>
          <button
            onClick={handleSaveSection}
            disabled={savingSection}
            data-testid="save-section-btn"
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-['Montserrat'] font-semibold text-sm transition-all ${savedSection ? "bg-green-500 text-white" : "bg-[#0072A4] text-white hover:bg-[#005A80]"} disabled:opacity-60`}
          >
            <Save size={14} />
            {savingSection ? "Guardando..." : savedSection ? "¡Guardado!" : "Guardar textos"}
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-[#0F172A] font-['Montserrat'] mb-1">Etiqueta (pill)</label>
            <input
              name="section_label"
              value={sectionData.section_label}
              onChange={handleSectionChange}
              data-testid="section-label-input"
              className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4]"
              placeholder="Nuestro Equipo"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#0F172A] font-['Montserrat'] mb-1">Título principal</label>
            <input
              name="title"
              value={sectionData.title}
              onChange={handleSectionChange}
              data-testid="section-title-input"
              className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4]"
              placeholder="Los mejores monitores"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-semibold text-[#0F172A] font-['Montserrat'] mb-1">Subtítulo / descripción</label>
            <input
              name="subtitle"
              value={sectionData.subtitle}
              onChange={handleSectionChange}
              data-testid="section-subtitle-input"
              className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4]"
              placeholder="Profesionales titulados y apasionados de la educación al aire libre."
            />
          </div>
        </div>
      </div>

      {/* ── Monitor form ─────────────────────────────────────────────────── */}
      {editing && (
        <div className="bg-white rounded-xl border-2 border-[#0072A4] p-6 mb-6" data-testid="monitor-form">
          <h3 className="font-['Montserrat'] font-semibold text-[#0F172A] mb-4">
            {editing === "__new__" ? "Nuevo monitor" : "Editar monitor"}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-[#0F172A] font-['Montserrat'] mb-1">Nombre completo *</label>
              <input value={form.nombre} onChange={e => setForm(p => ({ ...p, nombre: e.target.value }))} data-testid="monitor-nombre"
                className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4]" placeholder="Nombre y apellidos" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#0F172A] font-['Montserrat'] mb-1">Especialidad *</label>
              <input value={form.especialidad} onChange={e => setForm(p => ({ ...p, especialidad: e.target.value }))} data-testid="monitor-especialidad"
                className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4]" placeholder="Director del Campus, BTT..." />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-[#0F172A] font-['Montserrat'] mb-1">Descripción / Bio</label>
              <textarea value={form.descripcion} onChange={e => setForm(p => ({ ...p, descripcion: e.target.value }))} rows={3} data-testid="monitor-descripcion"
                className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4] resize-none" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-[#0F172A] font-['Montserrat'] mb-1">Foto (URL)</label>
              <input value={form.foto_url} onChange={e => setForm(p => ({ ...p, foto_url: e.target.value }))} data-testid="monitor-foto"
                className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4] mb-2" placeholder="https://..." />
              <label className="flex items-center gap-2 cursor-pointer bg-[#F0F8FF] px-3 py-2 rounded-lg text-sm text-[#0072A4] font-['Montserrat'] font-semibold hover:bg-[#E0F0FF] transition-colors w-fit">
                <Upload size={14} /> Subir foto
                <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" data-testid="monitor-photo-upload" />
              </label>
              {uploading && <p className="text-xs text-[#94A3B8] mt-1">Subiendo...</p>}
              {form.foto_url && (
                <img src={getImageUrl(form.foto_url)} alt="preview" className="mt-2 w-20 h-20 object-cover rounded-full border-2 border-[#E2E8F0]" onError={e => e.target.style.display = "none"} />
              )}
            </div>
          </div>
          <div className="flex gap-3 mt-4 pt-4 border-t border-[#E2E8F0]">
            <button onClick={handleSave} disabled={saving} data-testid="save-monitor-btn"
              className="flex items-center gap-2 bg-[#3CB44B] text-white font-['Montserrat'] font-semibold px-5 py-2 rounded-lg hover:bg-[#2E9B3A] transition-all text-sm disabled:opacity-60">
              <Save size={15} /> {saving ? "Guardando..." : "Guardar"}
            </button>
            <button onClick={handleCancel} data-testid="cancel-monitor-btn"
              className="flex items-center gap-2 px-5 py-2 rounded-lg border border-[#E2E8F0] text-[#475569] font-['Montserrat'] text-sm hover:bg-[#F8FAFC]">
              <X size={15} /> Cancelar
            </button>
          </div>
        </div>
      )}

      {/* ── Monitor list ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {monitors.map((monitor) => (
          <div key={monitor._id} data-testid={`monitor-item-${monitor._id}`}
            className="bg-white rounded-xl border border-[#E2E8F0] p-5 hover:shadow-md transition-shadow text-center">
            <div className="w-16 h-16 rounded-full mx-auto mb-3 overflow-hidden border-2 border-[#E2E8F0] bg-[#F0F8FF] flex items-center justify-center">
              {getImageUrl(monitor.foto_url) ? (
                <img src={getImageUrl(monitor.foto_url)} alt={monitor.nombre} className="w-full h-full object-cover" />
              ) : (
                <UserCircle size={40} color="#0072A4" />
              )}
            </div>
            <h3 className="font-['Montserrat'] font-semibold text-[#0F172A] text-sm mb-1">{monitor.nombre}</h3>
            <span className="inline-block bg-[#0072A4]/10 text-[#0072A4] text-xs font-['Montserrat'] font-semibold px-2 py-0.5 rounded-full mb-2">
              {monitor.especialidad}
            </span>
            {monitor.descripcion && (
              <p className="text-[#475569] font-['Montserrat'] text-xs line-clamp-2 mb-3">{monitor.descripcion}</p>
            )}
            <div className="flex justify-center gap-3">
              <button onClick={() => handleEdit(monitor)} data-testid={`edit-monitor-${monitor._id}`}
                className="flex items-center gap-1 text-[#0072A4] font-['Montserrat'] text-xs font-semibold hover:text-[#005A80]">
                <Pencil size={12} /> Editar
              </button>
              <button onClick={() => handleDelete(monitor._id)} data-testid={`delete-monitor-${monitor._id}`}
                className="flex items-center gap-1 text-red-500 font-['Montserrat'] text-xs font-semibold hover:text-red-700">
                <Trash2 size={12} /> Eliminar
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
